// ============================================================================
// TB-wise grossing up — helpers over the source-split response
// ============================================================================
// The API splits every consolidated ledger across the live source TBs using
// the consolidator's own rules (names matched ignoring capitals and edge
// spaces; an Opening TB feeds openings only). These helpers read that split
// for one ledger. No API imports, so jest runs them.

import type { TbSourceSplit, TbSplitAmount, TbSplitSource } from 'api/types/fsa/trialBalance.types';

/** The consolidation key: trimmed, lower-cased ledger name. */
export const ledgerKey = (name: string | null | undefined): string => (name ?? '').trim().toLowerCase();

/** A source's label: its name, else its file name. */
export const sourceLabel = (s: TbSplitSource): string => s.name?.trim() || s.originalFileName || 'Trial balance';

/** True when the split is worth showing (more than one live source). */
export const hasSplit = (split: TbSourceSplit | null | undefined): boolean => (split?.sources.length ?? 0) > 1;

/** Sources that feed CLOSING balances (current-year TBs) — the Notes columns. */
export const closingSources = (split: TbSourceSplit | null | undefined): TbSplitSource[] =>
  (split?.sources ?? []).filter(s => s.contributesClosing);

/** One ledger's amount per source, in source order; null where the source doesn't carry the ledger. */
export function ledgerSplit(split: TbSourceSplit | null | undefined, ledgerName: string): Array<{ source: TbSplitSource; amount: TbSplitAmount | null }> {
  const amounts = split?.ledgers[ledgerKey(ledgerName)] ?? [];
  return (split?.sources ?? []).map(source => ({ source, amount: amounts.find(a => a.sourceId === source.id) ?? null }));
}

/** True when a source spells the ledger differently from the consolidated name (capitals or edge spaces only — anything else would be a different ledger). */
export const spelledDifferently = (sourceName: string, consolidatedName: string): boolean =>
  sourceName.trim() !== consolidatedName.trim();
